import React, { useMemo, useState, useEffect } from "react";
import Modal from "../ui/Modal";

const input = { padding:"10px 12px", borderRadius:8, border:"1px solid #ddd", background:"#fff", fontSize:14 };
const area  = { ...input, resize:"vertical" };
const row   = { display:"grid", gridTemplateColumns:"140px 1fr", alignItems:"center", gap:12 };

// convert spaces->underscore, lowercase, collapse repeats, trim underscores
const sanitizeCode = (v) =>
  (v || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function AddMainCategoryModal({ open, onClose, onCreate }) {
  const [form, setForm] = useState({
    category: "",
    code: "",
    description: "",
    visibility: 1,
  });
  const [touched, setTouched] = useState({ category: false, code: false });
  const [saving, setSaving] = useState(false);

  // keep code sanitized live
  const set = (k, v) => {
    setForm((p) => {
      if (k === "code") v = sanitizeCode(v);
      return { ...p, [k]: v };
    });
  };

  // optional UX: if user types a name but leaves code empty, auto-fill code from name once
  useEffect(() => {
    if (form.category && !form.code) {
      setForm((p) => ({ ...p, code: sanitizeCode(form.category) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category]);

  const errors = {
    category: !form.category?.trim() ? "Name is required" : "",
    code: !form.code?.trim() ? "Code is required" : "",
  };
  const disabled = useMemo(
    () => !!errors.category || !!errors.code,
    [errors.category, errors.code]
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    setTouched({ category: true, code: true });
    if (disabled || saving) return;

    setSaving(true);
    try {
      await onCreate?.(form); // expects to throw on failure
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose?.()}
      title="Add Main Category"
      footer={
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
          <button disabled={saving} onClick={onClose} style={styles.btn}>Cancel</button>
          <button disabled={saving || disabled} onClick={submit} style={styles.primary}>
            {saving ? "Saving…" : "Add Category"}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} style={{ display:"grid", gap:12 }}>
        <div style={row}>
          <label>Name<span style={{color:"#b3261e"}}>*</span></label>
          <div>
            <input
              value={form.category}
              onChange={(e)=>set("category", e.target.value)}
              onBlur={()=>touch("category")}
              style={{ ...input, borderColor: touched.category && errors.category ? "#b3261e" : "#ddd" }}
              aria-invalid={!!(touched.category && errors.category)}
              placeholder="e.g., Beverages"
            />
            {touched.category && errors.category && (
              <div style={styles.err}>{errors.category}</div>
            )}
          </div>
        </div>

        <div style={row}>
          <label>Code<span style={{color:"#b3261e"}}>*</span></label>
          <div>
            <input
              value={form.code}
              onChange={(e)=>set("code", e.target.value)}
              onBlur={()=>touch("code")}
              style={{ ...input, fontFamily:"monospace", borderColor: touched.code && errors.code ? "#b3261e" : "#ddd" }}
              aria-invalid={!!(touched.code && errors.code)}
              placeholder="auto-fills from name, can edit"
            />
            <div style={{ fontSize:12, color:"#666", marginTop:4 }}>
              Code is stored lowercase; spaces & symbols become <code>_</code>.
            </div>
            {touched.code && errors.code && (
              <div style={styles.err}>{errors.code}</div>
            )}
          </div>
        </div>

        <div style={row}>
          <label>Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e)=>set("description", e.target.value)}
            style={area}
            placeholder="Optional"
          />
        </div>

        <div style={row}>
          <label>Visible</label>
          <input
            type="checkbox"
            checked={!!form.visibility}
            onChange={(e)=>set("visibility", e.target.checked ? 1 : 0)}
          />
        </div>
      </form>
    </Modal>
  );
}

const styles = {
  input,
  btn: { padding:"8px 12px", borderRadius:8, border:"1px solid #ddd", background:"#fff", cursor:"pointer" },
  primary: { padding:"8px 12px", borderRadius:8, border: '1px solid rgba(255, 0, 0, 1)',
  background: 'rgba(255, 0, 0, 1)', color:"#fff", cursor:"pointer" },
  err: { color:"#b3261e", fontSize:12, marginTop:4 }
};
